import { useCallback, useRef } from 'react'
import {
    getPrivyLoginMethodFromLocalStorage,
    useEmbeddedWallet,
    useGetSignUpWalletSigner,
} from '@towns/privy'
import { getAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import {
    TransactionStatus,
    TSigner,
    useBaseReads,
    useBaseWrites,
    useLinkSmartAccountAndEOAAndSetDefaultWallet,
    WalletAlreadyLinkedError,
} from 'use-towns-client'
import { Address, isAddress } from 'viem'

type LinkEOAResult = {
    /**
     * The signer to use for subsequent operations.
     * Will be the EOA signer if linking occurred, otherwise the original signer.
     */
    signerToUse: TSigner
}

const WALLET_ALREADY_LINKED_ERROR_MESSAGE = new WalletAlreadyLinkedError().message

export function useIsSmartAccountAlreadyLinked() {
    const { userOpsInstance } = useBaseWrites()
    const { embeddedWallet } = useEmbeddedWallet()
    const rootKeyAddress = embeddedWallet?.address
    const { checkIfLinked } = useBaseReads()

    return useCallback(async () => {
        if (!rootKeyAddress) {
            throw new Error('rootKeyAddress is not set')
        }

        if (!isAddress(rootKeyAddress)) {
            throw new Error('rootKeyAddress is not a valid address')
        }

        if (!userOpsInstance) {
            throw new Error('userOpsInstance is not available')
        }

        const smartAccountAddress = await getAbstractAccountAddress({
            rootKeyAddress,
            isMyAbstractAccount: true,
            isAccountAbstractionEnabled: true,
            userOpsInstance,
        })

        if (!smartAccountAddress) {
            throw new Error('Could not calculate abstract account address')
        }

        return {
            isLinked: await checkIfLinked(rootKeyAddress, smartAccountAddress),
            smartAccountAddress,
            rootKeyAddress,
        }
    }, [rootKeyAddress, userOpsInstance, checkIfLinked])
}

/**
 * Hook to handle linking EOA wallets for new users during sign-up flow.
 * This combines the logic for:
 * 1. Checking if user needs EOA linking
 * 2. Getting the sign-up wallet signer
 * 3. Calculating abstract account address
 * 4. Performing the link operation
 *
 * @returns A function that attempts to link EOA and returns the appropriate signer to use
 */
export function useLinkEOAOnSignUp() {
    // root key == privy embedded wallet == towns user id
    const signUpWalletSigner = useGetSignUpWalletSigner()
    const { execute: linkSmartAccountAndEOAAndSetDefaultWallet } =
        useLinkSmartAccountAndEOAAndSetDefaultWallet()
    const { baseChain } = useEnvironment()

    const signUpWalletSignerRef = useRef(signUpWalletSigner)
    // update the ref so the closure in useCallback calls with latest values
    signUpWalletSignerRef.current = signUpWalletSigner

    return useCallback(
        async (args: {
            rootKeyAddress: Address
            signer: TSigner
            smartAccountAddress: Address
            smartAccountAlreadyLinked: boolean
        }): Promise<LinkEOAResult> => {
            const { signer, smartAccountAddress, smartAccountAlreadyLinked, rootKeyAddress } = args
            // If smart account is already linked no EOA linking needed
            if (smartAccountAlreadyLinked) {
                return { signerToUse: signer }
            }

            // New users: try to get the EOA signer from sign-up wallet
            let eoaSignerToLink = await signUpWalletSignerRef.current.getSigner(baseChain.id)

            // If no EOA signer available, check if user logged in with SIWE and retry
            // NOTE: Most users (social login) won't have an EOA signer - this is expected
            if (!eoaSignerToLink) {
                const loginMethod = getPrivyLoginMethodFromLocalStorage()
                const isSiweLogin = loginMethod === 'siwe'
                // Only retry and log for SIWE users - they SHOULD have an EOA signer
                if (isSiweLogin) {
                    console.log(
                        '[useLinkEOAOnSignUp] SIWE login detected but no EOA signer found, retrying...',
                        {
                            loginMethod,
                            rootKeyAddress,
                            smartAccountAddress,
                            baseChainId: baseChain.id,
                        },
                    )

                    const retryDelay = 500
                    const maxRetries = Math.floor(4000 / retryDelay)
                    for (let i = 0; i < maxRetries; i++) {
                        await new Promise((resolve) => setTimeout(resolve, retryDelay))

                        const { ready, getSigner } = signUpWalletSignerRef.current
                        if (!ready) {
                            console.log('[useLinkEOAOnSignUp] Privy not ready, skipping retry', {
                                retryAttempt: i + 1,
                                maxRetries,
                            })
                            continue
                        }

                        eoaSignerToLink = await getSigner(baseChain.id)
                        if (eoaSignerToLink) {
                            console.log(
                                `[useLinkEOAOnSignUp] Successfully retrieved EOA signer after ${(i + 1) * retryDelay}ms`,
                            )
                            break
                        }
                    }

                    if (!eoaSignerToLink) {
                        console.error(
                            '[useLinkEOAOnSignUp] UNEXPECTED: User logged in via SIWE but EOA signer not found after retries',
                            {
                                loginMethod,
                                rootKeyAddress,
                                smartAccountAddress,
                                baseChainId: baseChain.id,
                                retriesAttempted: maxRetries,
                            },
                        )
                    }
                }

                // Only return early if we STILL don't have the EOA signer after retries
                if (!eoaSignerToLink) {
                    // return the passed signer if no EOA signer was found so we don't interrupt sign ups
                    return { signerToUse: signer }
                }
            }

            // Perform the link operation
            const linkResult = await linkSmartAccountAndEOAAndSetDefaultWallet({
                userId: rootKeyAddress,
                signer: signer,
                smartAccountAddress,
                externalWalletSigner: eoaSignerToLink,
            })

            if (!linkResult) {
                throw new Error('Failed to link smart account and EOA')
            }

            if (linkResult.error) {
                if (
                    linkResult.error instanceof WalletAlreadyLinkedError ||
                    linkResult.error.message.includes(WALLET_ALREADY_LINKED_ERROR_MESSAGE)
                ) {
                    console.log(
                        '[useLinkEOAOnSignUp] new user signing up with EOA linked to another account, returning root key signer',
                    )
                    return { signerToUse: signer }
                }

                throw linkResult.error
            }

            if (linkResult.status === TransactionStatus.Failed) {
                throw new Error('Link transaction did not complete successfully')
            }

            // Return the EOA signer for subsequent operations
            return { signerToUse: eoaSignerToLink }
        },
        [baseChain.id, linkSmartAccountAndEOAAndSetDefaultWallet],
    )
}
